import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { FileText, Calendar, Scale, User } from 'lucide-react';

interface CaseTooltipProps {
  caseData: {
    id: string;
    title: string;
    case_number?: string;
    case_number_cnj?: string;
    case_type?: string;
    status?: string;
    court?: string;
    filing_date?: string;
    clients?: { name: string; email: string };
  };
  children: React.ReactNode;
}

export const CaseTooltip: React.FC<CaseTooltipProps> = ({ caseData, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // Debug: verificar dados recebidos
  console.log('CaseTooltip - caseData:', caseData);

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setIsVisible(true);
  };

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && caseData && createPortal(
        <div 
          className="fixed w-max pointer-events-none"
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            transform: 'translate(-50%, -100%)',
            zIndex: 999999
          }}
        >
          <Card className="w-80 shadow-2xl border border-gray-200 bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                {caseData.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {/* Número do Processo */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">Processo:</span>
                <span className="text-xs font-mono text-blue-700">
                  {caseData.case_number_cnj || caseData.case_number || 'Sem número'}
                </span>
              </div>

              {/* Tipo e Status */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">Tipo:</span>
                <Badge variant="outline" className="text-xs">
                  {caseData.case_type || 'Não informado'}
                </Badge>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">Status:</span>
                <Badge 
                  variant="outline" 
                  className={`text-xs ${
                    caseData.status === 'Em andamento' ? 'border-green-300 text-green-700' :
                    caseData.status === 'Arquivado' ? 'border-gray-300 text-gray-700' :
                    caseData.status === 'Suspenso' ? 'border-yellow-300 text-yellow-700' :
                    'border-blue-300 text-blue-700'
                  }`}
                >
                  {caseData.status || 'Não informado'}
                </Badge>
              </div>

              {/* Tribunal */}
              {caseData.court && (
                <div className="flex items-start gap-2">
                  <Scale className="w-3 h-3 mt-0.5 text-gray-500" />
                  <div>
                    <span className="text-xs font-medium text-gray-600">Tribunal:</span>
                    <p className="text-xs text-gray-700">{caseData.court}</p>
                  </div>
                </div>
              )}

              {/* Data de Autuação */}
              {caseData.filing_date && (
                <div className="flex items-start gap-2">
                  <Calendar className="w-3 h-3 mt-0.5 text-gray-500" />
                  <div>
                    <span className="text-xs font-medium text-gray-600">Autuação:</span>
                    <p className="text-xs text-gray-700">
                      {format(parseISO(caseData.filing_date), 'dd/MM/yyyy', { locale: ptBR })}
                    </p>
                  </div>
                </div>
              )}

              {/* Cliente */}
              {caseData.clients && (
                <div className="flex items-start gap-2">
                  <User className="w-3 h-3 mt-0.5 text-gray-500" />
                  <div>
                    <span className="text-xs font-medium text-gray-600">Cliente:</span>
                    <p className="text-xs text-gray-700">{caseData.clients.name}</p>
                  </div>
                </div>
              )}

              {/* Instrução para clicar */}
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500 text-center">
                  Clique no número do processo para visualizar
                </p>
              </div>
            </CardContent>
          </Card>
        </div>,
        document.body
      )}
    </div>
  );
};
